import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import OutsideClickHandler from "react-outside-click-handler";

import { isMobile } from "../../../../utils/responsive";
import { getTimespanFromPeriod } from "../../../DataDownloadComponents/TimespanPicker/utils";
import { BaseDateInput } from "../../../FormElement/BaseDateInput";
import DropdownSelect from "../../../FormElement/DropdownSelect";
import { DEFAULT_PERIOD_OPTION, PERIOD_OPTIONS } from "../const";
import { PeriodOptionType, PeriodOptionSchema } from "../types";

type Timespan = {
  start_date: Date | null;
  end_date: Date | null;
};

interface ITimespanPickerProps {
  timespan: Timespan;
  setTimespan: (timespan: Timespan) => void;
}

export const TimespanPicker: React.FC<ITimespanPickerProps> = (props) => {
  const { timespan, setTimespan } = props;
  const { t: _t } = useTranslation();
  const tCommon = (key: string) => _t(`common.dropdown.${key}`);

  const minDate = useMemo(() => dayjs().subtract(18, "months").toDate(), []);

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [period, setPeriod] = useState<PeriodOptionType>(DEFAULT_PERIOD_OPTION);

  const handleChangePeriod = (value: string) => {
    const periodParse = PeriodOptionSchema.safeParse(value);
    if (!periodParse.success) {
      return;
    }
    const period = periodParse.data;
    setPeriod(period);
    const newTimespan = getTimespanFromPeriod(period);
    setTimespan({
      start_date: newTimespan.startDate?.toDate() ?? new Date(),
      end_date: newTimespan.endDate?.toDate() ?? new Date(),
    });
  };

  const handleChangeStartDate = (date: string | Date | null) => {
    // Cancel if not in custom period mode or date is null
    if (period !== "custom" || !date) {
      return;
    }

    // Set to minDate if date is before minDate
    if (dayjs(date).isBefore(minDate)) {
      setTimespan({
        start_date: minDate,
        end_date: timespan.end_date,
      });
      return;
    }

    // Set to current endDate if date is after current endDate
    if (dayjs(date).isAfter(timespan.end_date)) {
      setTimespan({
        start_date: timespan.start_date,
        end_date: dayjs(date).toDate(),
      });
      return;
    }

    setTimespan({
      start_date: dayjs(date).toDate(),
      end_date: timespan.end_date,
    });
  };

  const handleChangeEndDate = (date: string | Date | null) => {
    // Cancel if not in custom period mode or date is null
    if (period !== "custom" || !date) {
      return;
    }

    // Set to current startDate if date is before current startDate
    if (dayjs(date).isBefore(timespan.start_date)) {
      setTimespan({
        start_date: timespan.start_date,
        end_date: dayjs(date).toDate(),
      });
      return;
    }

    // Set to minDate if date is before minDate
    if (dayjs(date).isBefore(minDate)) {
      setTimespan({
        start_date: timespan.start_date,
        end_date: minDate,
      });
      return;
    }

    setTimespan({
      start_date: timespan.start_date,
      end_date: dayjs(date).toDate(),
    });
  };

  // For updating both at once
  // Avoids an issue when changing startDate and endDate separately,
  // and new values are validated against old values.
  const handleChangeTimespan = (
    startDate: string | Date | null,
    endDate: string | Date | null
  ) => {
    // Cancel if not in custom period mode
    if (period !== "custom") {
      return;
    }

    // If both are null, set both to null and return early
    if (startDate === null && endDate === null) {
      setTimespan({
        start_date: null,
        end_date: null,
      });
      return;
    }

    const startDayJs = startDate ? dayjs(startDate) : null;
    const endDayJs = endDate ? dayjs(endDate) : null;

    // If startDate is given, validate it against minDate
    if (startDayJs !== null && startDayJs.isBefore(minDate)) {
      return;
    }

    // If endDate is given, validate it against minDate
    if (endDayJs !== null && endDayJs.isBefore(minDate)) {
      return;
    }

    // If both are given, validate them against each other
    if (
      startDayJs !== null &&
      endDayJs !== null &&
      startDayJs.isAfter(endDayJs)
    ) {
      return;
    }

    setTimespan({
      start_date: startDayJs?.toDate() ?? null,
      end_date: endDayJs?.toDate() ?? null,
    });
  };

  return (
    <div className="hook-form-timespan-picker">
      <OutsideClickHandler onOutsideClick={() => setShowPopup(false)}>
        <div className="date-drop">
          <button
            type="button"
            className="btn btn-gray-border"
            onClick={() => {
              setShowPopup((v) => !v);
            }}
          >
            <span className="bold">{tCommon(period)}</span>
            <div className="date-txt">
              {dayjs(timespan.start_date).format("YYYY-MM-DD")}
              {" - "}
              {dayjs(timespan.end_date).format("YYYY-MM-DD")}
            </div>
            <i className={`icons icon-drop ${showPopup ? "open" : ""}`}>
              &nbsp;
            </i>
          </button>
        </div>
        {showPopup && (
          <div className="date-panel">
            <div className="period-select">
              <div className="period-select-wrap">
                <DropdownSelect
                  fieldLabel={""}
                  value={period}
                  isTranslation={true}
                  items={PERIOD_OPTIONS}
                  classNameContainer={"sort-dropdown"}
                  onChange={(value: string) => {
                    handleChangePeriod(value as PeriodOptionType);
                  }}
                />
              </div>
              <div
                className={`date-input-wrap ${
                  period !== "custom" ? "disabled" : ""
                }`}
              >
                <BaseDateInput
                  disabled={period !== "custom"}
                  fieldLabel={""}
                  value={timespan.start_date ?? null}
                  placeholder={""}
                  minDate={minDate}
                  maxDate={timespan.end_date ?? undefined}
                  onChange={(newDate) => {
                    handleChangeStartDate(newDate as Date);
                  }}
                />
                <span className="to">~</span>
                <BaseDateInput
                  disabled={period !== "custom"}
                  fieldLabel={""}
                  value={timespan.end_date ?? null}
                  placeholder={""}
                  minDate={timespan.start_date ?? undefined}
                  onChange={(newDate) => {
                    handleChangeEndDate(newDate as Date);
                  }}
                />
              </div>
            </div>
            <div
              className={`custom-timespan-select ${
                period !== "custom" ? "disabled" : ""
              }`}
            >
              <ReactDatePicker
                disabled={period !== "custom"}
                locale="ja"
                onChange={(dates: [Date | null, Date | null]) => {
                  handleChangeTimespan(dates[0], dates[1]);
                }}
                minDate={minDate}
                selectsRange
                inline
                startDate={timespan.start_date ?? undefined}
                endDate={timespan.end_date ?? undefined}
                monthsShown={isMobile() ? 1 : 2}
              />
            </div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  );
};
